import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { IMessageType } from "@/v1/types";
import { showErrorMessage, showMessage } from "@/v1/utils";
import { CustomerResolution, resolveCustomer } from "./resolveCustomer";

/**
 * Resolves the typed text and reports the selected customer's email, or
 * `undefined` when the box was emptied or nothing unique matched. Zero or
 * several matches never select anybody; the operator sees why instead.
 */
export const useCustomerSearch = (
  onSelect: (email: string | undefined) => void
) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  // `search` keeps one identity, so a caller may run it from an effect without
  // looping when `onSelect` or `t` are recreated on every render.
  const latest = useRef({ onSelect, t });
  latest.current = { onSelect, t };
  // Only the newest search may select or stop the spinner: an older request
  // that answers late must not overwrite a newer selection.
  const lastRequestId = useRef(0);

  const search = useCallback(
    async (input: string): Promise<CustomerResolution | undefined> => {
      const { onSelect, t } = latest.current;
      const requestId = ++lastRequestId.current;
      const isStale = () => requestId !== lastRequestId.current;
      setLoading(true);
      try {
        const resolution = await resolveCustomer(input);
        if (isStale()) {
          return undefined;
        }
        if (resolution.kind === "none" || resolution.kind === "several") {
          showMessage({
            type: IMessageType.INFO,
            content: t(`customerSearch.${resolution.kind}`),
          });
        }
        onSelect(resolution.kind === "one" ? resolution.user.email : undefined);
        return resolution;
      } catch (error) {
        if (!isStale()) {
          showErrorMessage(t("customerSearch.failed"));
        }
        return undefined;
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    },
    []
  );

  return { search, loading };
};
